import React, { useCallback, useEffect, useState, useId, useContext, forwardRef, useImperativeHandle } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Input, Label, Table } from "reactstrap";
import { EXPIRE_POSITION_ROLE, GET_ORG_POSITIONS, GET_ORG_ROLES } from "../../../../graphql/position-gql";
import { IOrg } from "../../../../types/org";
import { AddIcon, CancelIcon, CheckIcon, DeleteIcon } from "../../../common/icons";
import { IPosition } from "../../../../types/position";
import { messages } from "../../../../i18n/message";
import { LOCALES } from "../../../../i18n/locales";
import { IIndividual } from "../../../../types/individual";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { CheckGroup } from "../../../../hook/useCheckGroup";
import { CategoryType } from "../../../../types/constants";
import { notifySuccess } from "../../../../services/notification";
import confirm from "../../../common/confirm";
import PositionRoleCreateModal from "../../position-role-create/position-role-create";
import useAuth from "../../../../hook/useAuth";
import { OrgReducerContext } from "../../org-context";
import Button from "../../../common/button";
import { PositionIndividualsItem } from "../common-table-components";

type PositionsIndividualsProps = {
  org: IOrg;
  positionSelected?: (position: IPosition) => void;
  individualSelected?: (individual: IIndividual,positionRole:IPosition) => void;
  checkGroup: CheckGroup<number>;
  lastSelectedIndividual: (individual: IIndividual,positionRole:IPosition) => any;
};

export type PositionListRef = {
  getPositonList:() => IPosition[];
};

const PositionsIndividuals = forwardRef<PositionListRef,PositionsIndividualsProps>((props,ref) => { 
  const {org, positionSelected, individualSelected, checkGroup, lastSelectedIndividual}= props;
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [isRole, setIsRole] = useState<boolean>(false)
  const toggleDropdownOpen = useCallback(() => {setDropdownOpen(!dropdownOpen)}, [dropdownOpen])
  const [expirePositonRole,{loading:expireLoading}] = useMutation(EXPIRE_POSITION_ROLE)
  const [selectedPosition,setSelectedPosition] = useState<IPosition>()
  const [createPositionOpen, setCreatePositionOpen] = useState<boolean>(false)
  const [cloned,setCloned] = useState<boolean>(false);
  const toggleCreatePosition = useCallback(() => {setCreatePositionOpen(!createPositionOpen)}, [createPositionOpen])
  const [state, dispatch] = useContext(OrgReducerContext);
  const [getOrgPositions, { loading, data }] = useLazyQuery(GET_ORG_POSITIONS, {
    onCompleted: (data) => {
      //console.log("data:",data)
        if (data?.org?.positions) {
            checkGroup.init(data?.org.positions.map((e: IPosition) => e.positionId))
        }
    }
  });

  const { isFrenchTranslator } = useAuth();

  useEffect(() => {
    setCloned(!!org.details?.shareLinkId)
    getOrgPositions({ variables: { orgId: org.id },fetchPolicy: "network-only" });
  }, [org]);
  
  const getPositonList = () =>{
    const checkedValues = checkGroup.getCheckedValues();
    if(checkedValues&&checkedValues.length>0){
      return data?.org.positions.filter((pos:IPosition)=>checkedValues.includes(pos.positionId))
    }
    return null;
  }

  useImperativeHandle(ref, () => ({
    getPositonList
  }));

  const onCreatePositionHandle = ()=>{
    setIsRole(false)
    toggleCreatePosition();
}
  const expireRefetchQueries = () => {
    return [{query: GET_ORG_ROLES, variables: {  orgId: org.id||0 } },{query: GET_ORG_POSITIONS, variables: {  orgId: org.id||0 } }]
}

  const onExpirePositionOrRoleHandle = () => {
    expirePositionOrRoleConfirm().then(() => {
      expirePositionOrRoleExecution()
    })
  }

  const expirePositionOrRoleConfirm = () => {
    return confirm({
      title: 'Expire selected Position(s) or Role(s)',
      content: messages[LOCALES.ENGLISH].INFO['4.1'],
      okText: <><CheckIcon /> Expire</>,
      okBtnColor: 'danger',
      titleCenter: true,
      bodyCenter: true
    })
  }

  const expirePositionOrRoleExecution = () => {
    const positionIds = checkGroup.getCheckedValues()
    expirePositonRole({
      variables: { orgId: org?.id || 0, positionIds: positionIds },
      onCompleted: (data) => {
        if (data?.expirePositionOrRole && !data.expirePositionOrRole.success) {
          expireValidationConfirm(data.expirePositionOrRole.message)
        } else {
          setSelectedPosition(undefined);
          notifySuccess();
        }
      },
      awaitRefetchQueries: true,
      refetchQueries: expireRefetchQueries()
    })
  }

  const expireValidationConfirm = (message: string) => {
    return confirm({
      title: 'Warning!',
      content: message,
      okText: <><CancelIcon /> Close</>,
      okBtnColor: 'primary',
      titleCenter: true,
      bodyCenter: true,
      showCancel: false
    })
  }

  return (
    <div id="positions-individuals-panel">
      { loading && <SpinnerOverlay /> }
      { <PositionRoleCreateModal isOpen={createPositionOpen} toggle={toggleCreatePosition} isRole={isRole} org={org} /> }
      { org.details?.category?.type === CategoryType.FORMLGROUP && !isFrenchTranslator && 
        <div className="py-3">
          <Button toggle={toggleDropdownOpen} color="primary" style={{ marginRight: '15px' }} onClick={onCreatePositionHandle} disabled={cloned}>
            <AddIcon />
            &nbsp;<span role="region" aria-label="Create" >Add</span>
          </Button>
          <Button color="danger" style={{ marginRight: '15px' }}
            disabled={checkGroup.isNoneChecked()||cloned }
            onClick={onExpirePositionOrRoleHandle}>
            <DeleteIcon />
            &nbsp;<span role="region" aria-label="Expire">Expire</span>
          </Button>
        </div>
      }
      { data?.org?.positions?.length===0 && (<div role="region" aria-label="No records found">{messages[LOCALES.ENGLISH].INFO['1.1']}</div>) }
      { data?.org?.positions?.length>0 && 
        <Table className="organizations-table fixed table-striped-rowspan" id="org_positions_individuals">
          <thead>
            <tr>
              { !isFrenchTranslator && 
                <th className="icon-column">
                  <Label for="checkAll" className="hidden-text" role="region" aria-label="Select All">Select All</Label>
                  <Input id="checkAll" aria-label="Select All" className="form-check-input" type='checkbox'
                    checked={checkGroup.isAllChecked()} onChange={checkGroup.onCheckAll} disabled={cloned}/>
                </th>
              }
              <th>Position Title</th>
              <th className="column-15rem">Assignment Type</th>
              <th>Individual Name</th>
            </tr>
          </thead>
          { data.org.positions.map((position: IPosition, index: number) => (
            <PositionIndividualsItem 
              position={position} 
              org={org} 
              positionSelected={positionSelected}
              individualSelected={individualSelected}
              lastSelectedIndividual={lastSelectedIndividual}
              state={state} 
              isFrenchTranslator={isFrenchTranslator} 
              checkGroup={checkGroup} 
              cloned={cloned}
              index={index}
              key={index} />
          ))}
        </Table>
      }
    </div>
  )
});

export default PositionsIndividuals;
